import { useCallback, useEffect, useRef, useState } from "react";
import { getBinding } from "../../lib/binding";
import type {
  RealtimeEvent,
  TvCallDirectoryEntry,
  TvCallDirectoryReason,
  TvCallDirectoryResponse,
} from "@shared/types/foyer";

/**
 * The faces this room's resident may call (VC-08).
 *
 * Same shape as the other care hooks: screen-token authed against the paired
 * binding, or a plain fetch to the previewed room in the CMS. Polls every 60 s
 * - the ETag makes an unchanged directory a 304 with no body - and refetches
 * IMMEDIATELY on a `call_directory_changed` event with `cache: "reload"`, which
 * is the whole point of the event: a revoked relative must disappear now, not
 * after the 10-second cache window.
 *
 * A premise-scoped event carries no `roomId`; that is every room's business, so
 * it always applies. A room-scoped one only applies to its own room.
 *
 * `reason` is only meaningful when `entries` is empty; the screen turns it into
 * a Swedish sentence. `ready` says the first answer has landed, so the grid can
 * hold still rather than flashing an empty state on the way in. `unavailable`
 * is the fail-closed signal for "we have never gotten a real answer at all" -
 * see its own doc below for why that must never be read the same as
 * `reason: null`.
 */
export function useCallDirectory(
  active: boolean,
  realtimeEvent?: RealtimeEvent | null,
  previewPremiseId?: string,
  previewRoomId?: string
): {
  entries: TvCallDirectoryEntry[];
  reason: TvCallDirectoryReason | null;
  ready: boolean;
  error: string | null;
  /**
   * True when we have never once received a real answer (a 2xx/304 from the
   * server) - as opposed to `reason: null`, which means the server DID answer
   * and said nothing hides the tile. The two must never be conflated: treating
   * "we don't know" as "we know it's fine" is exactly the bug that let the
   * Videosamtal tile show in the CMS preview regardless of the room's real
   * entitlement/occupancy/policy state, because the preview's first fetch
   * always 401ed against the screen-only route it used to call.
   */
  unavailable: boolean;
} {
  const [entries, setEntries] = useState<TvCallDirectoryEntry[]>([]);
  const [reason, setReason] = useState<TvCallDirectoryReason | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Mirrors `unavailable` but readable synchronously inside refresh() without
  // being a `useState` (same reason as `hasResult` in useVilmerActivities): an
  // error AFTER a real answer already landed must not re-hide whatever is
  // already correctly on screen just because one poll hiccuped.
  const hasAnswer = useRef(false);

  /** URL + headers to ask for this room's directory - or null when there is
   *  no room to ask about at all (the generic `?scheme=care` design preview). */
  const target = useCallback((): {
    url: string;
    roomId: string;
    headers: Record<string, string>;
  } | null => {
    const binding = getBinding();
    if (binding) {
      const headers: Record<string, string> = {};
      if (binding.screenToken) headers["X-Screen-Token"] = binding.screenToken;
      return {
        url: `/api/foyer/premise/${binding.premiseId}/rooms/${binding.roomId}/call-directory`,
        roomId: binding.roomId,
        headers,
      };
    }
    if (previewPremiseId && previewRoomId) {
      // No device binding - this is the CMS "Open preview" popup, proven by
      // its CMS session cookie rather than a screen token. The TV route above
      // is deliberately screen-only (a CMS session must never poll as a
      // screen), so the preview asks the CMS-session twin that answers the
      // identical question - see the `/preview` route in callContacts.ts.
      // Hitting the screen-only route here would 401 every single time and
      // (before this fix) silently defaulted to "nothing hides it".
      return {
        url: `/api/foyer/premise/${previewPremiseId}/rooms/${previewRoomId}/call-directory/preview`,
        roomId: previewRoomId,
        headers: {},
      };
    }
    return null;
  }, [previewPremiseId, previewRoomId]);

  const refresh = useCallback(
    async (force = false) => {
      const t = target();
      if (!t) {
        // Generic (no-room) preview: nothing to ask about. Mark ready so the
        // screen shows a real empty state instead of a permanent spinner.
        setReady(true);
        return;
      }
      try {
        const res = await fetch(t.url, {
          headers: t.headers,
          ...(force ? { cache: "reload" as const } : {}),
        });
        if (res.ok) {
          const body = (await res.json()) as TvCallDirectoryResponse;
          hasAnswer.current = true;
          setUnavailable(false);
          setEntries(Array.isArray(body.entries) ? body.entries : []);
          setReason(body.reason ?? null);
          setError(null);
        } else if (res.status !== 304) {
          setError(`HTTP ${res.status}`);
          if (!hasAnswer.current) setUnavailable(true);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        if (!hasAnswer.current) setUnavailable(true);
      } finally {
        setReady(true);
      }
    },
    [target]
  );

  useEffect(() => {
    if (!active) return;
    void refresh();
    intervalRef.current = setInterval(() => void refresh(), 60_000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [active, refresh]);

  useEffect(() => {
    if (!active) return;
    if (!realtimeEvent || realtimeEvent.type !== "call_directory_changed") {
      return;
    }
    const t = target();
    // Room-scoped event for a different room - not ours.
    if (realtimeEvent.roomId && t && realtimeEvent.roomId !== t.roomId) return;
    void refresh(true);
  }, [active, realtimeEvent, refresh, target]);

  return { entries, reason, ready, error, unavailable };
}

/**
 * The "no" the resident cannot act on - the feature is simply not there. These
 * hide the screen and its home tile entirely: a button a resident can press but
 * nobody in the room can fix is worse than no button.
 *
 * Four are premise-level policy (an operator switch, or no occupancy).
 * `not_entitled` (VC2-2/VC2-7) is the commercial one: the premise holds no site
 * license and this resident has no seat. It hides for the same reason and one
 * more - the alternative is an upsell on a care resident's TV, asking them to
 * buy something they cannot buy. Staff see the seat control in the CMS instead.
 *
 * `media_plane_unavailable` (VC3-7) is the operational one: the server holds no
 * media-plane credentials, so no call it offered could connect. It HIDES rather
 * than explaining, and that is a deliberate choice about audience. "The phone
 * system is down" is staff information: a resident cannot act on it, cannot
 * report it to anyone who can, and reading it on their own television turns an
 * operations gap into anxiety. The people who can fix it are told by the boot
 * log and by the fleet health alert, and until they do the tile is simply not
 * there - the same nothing every other premise-level "no" produces.
 *
 * The resident's own opt-out and "nobody has been added yet" are deliberately
 * NOT in this list - those are states staff can fix while looking at the TV, so
 * the screen stays and explains itself.
 */
const PREMISE_LEVEL_OFF: TvCallDirectoryReason[] = [
  "not_a_care_premise",
  "calls_disabled",
  "outbound_disabled",
  "media_plane_unavailable",
  "no_active_resident",
  "not_entitled",
];

export function callsHiddenForPremise(
  reason: TvCallDirectoryReason | null
): boolean {
  return reason != null && PREMISE_LEVEL_OFF.includes(reason);
}
